import React, {
   memo,
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";

import StudentWeekToolbar from "./StudentWeekToolbar";
import {
   formatDayHeader,
   formatVisibleDaysLabel,
   generateCalendarDays,
   getCenteredStartIndex,
   indexEventsByDaySlot,
   isToday,
   STUDENT_LESSON_TIME_SLOTS,
} from "../../utils/studentCalendarUtils";

const MOBILE_MAX_WIDTH = 767.98;
const SWIPE_THRESHOLD_PX = 48;
const EMPTY_SLOT_MAP = new Map();

function useVisibleDayCount() {
   const [count, setCount] = useState(7);

   useEffect(() => {
      const media = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
      const update = () => setCount(media.matches ? 3 : 7);
      update();
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
   }, []);

   return count;
}

function getSlotStatusClass(item) {
   if (!item) return "";

   if (item.kind === "lesson") {
      if (item.inactive) return "sWeekCal__slot--status-cancelled";
      return "sWeekCal__slot--status-lesson";
   }

   if (item.kind === "practicalExam") {
      if (item.status) {
         return `sWeekCal__slot--status-${String(item.status).toLowerCase()}`;
      }
      return "sWeekCal__slot--status-scheduled";
   }

   return "";
}

const CalendarSlot = memo(function CalendarSlot({ slotLabel, event, onSelectEvent }) {
   if (!event?.cardItem) {
      return (
         <div className="sWeekCal__slot sWeekCal__slot--empty">
            <span className="sWeekCal__slotHour">{slotLabel}</span>
         </div>
      );
   }

   const item = event.cardItem;
   const statusClass = getSlotStatusClass(item);

   return (
      <button
         type="button"
         className={[
            "sWeekCal__slot",
            "sWeekCal__slot--busy",
            statusClass,
         ]
            .filter(Boolean)
            .join(" ")}
         onClick={() => onSelectEvent?.(event)}
      >
         <span className="sWeekCal__slotType">{event.typeLabel}</span>
         <span className="sWeekCal__slotTime">{item.time}</span>
         <span className="sWeekCal__slotExtra">{item.extra}</span>
      </button>
   );
});

const DayColumn = memo(function DayColumn({ day, slotMap, onSelectEvent }) {
   const { day: dayNum, weekday } = formatDayHeader(day);
   const today = isToday(day);

   return (
      <article className={`sWeekCal__day${today ? " is-today" : ""}`}>
         <div className="sWeekCal__dayHeadWrap">
            <header className={`sWeekCal__dayHead${today ? " is-today" : ""}`}>
               <span className="sWeekCal__dayLabel">
                  {dayNum} {weekday}
               </span>
            </header>
         </div>

         <div className="sWeekCal__hours">
            {STUDENT_LESSON_TIME_SLOTS.map((slotLabel) => (
               <CalendarSlot
                  key={slotLabel}
                  slotLabel={slotLabel}
                  event={slotMap.get(slotLabel) ?? null}
                  onSelectEvent={onSelectEvent}
               />
            ))}
         </div>
      </article>
   );
});

export default function StudentWeekCalendar({
   events = [],
   onSelectEvent,
   initialDate = new Date(),
}) {
   const visibleCount = useVisibleDayCount();
   const allDays = useMemo(
      () => generateCalendarDays(initialDate, 90, 90),
      [initialDate],
   );
   const eventsByDay = useMemo(() => indexEventsByDaySlot(events), [events]);
   const [startIndex, setStartIndex] = useState(() =>
      getCenteredStartIndex(generateCalendarDays(initialDate, 90, 90), 7, new Date()),
   );
   const touchStartX = useRef(null);

   const maxStartIndex = Math.max(0, allDays.length - visibleCount);

   useEffect(() => {
      setStartIndex((current) => Math.min(current, maxStartIndex));
   }, [maxStartIndex]);

   const visibleDays = useMemo(
      () => allDays.slice(startIndex, startIndex + visibleCount),
      [allDays, startIndex, visibleCount],
   );

   const rangeLabel = useMemo(
      () => formatVisibleDaysLabel(visibleDays),
      [visibleDays],
   );

   const shiftIndex = useCallback(
      (delta) => {
         setStartIndex((current) => {
            if (delta < 0) return Math.max(0, current + delta);
            return Math.min(maxStartIndex, current + delta);
         });
      },
      [maxStartIndex],
   );

   const goPrev = useCallback(() => shiftIndex(-1), [shiftIndex]);
   const goNext = useCallback(() => shiftIndex(1), [shiftIndex]);

   const goToday = useCallback(() => {
      setStartIndex(getCenteredStartIndex(allDays, visibleCount, new Date()));
   }, [allDays, visibleCount]);

   const onTouchStart = (event) => {
      touchStartX.current = event.touches[0]?.clientX ?? null;
   };

   const onTouchEnd = (event) => {
      if (touchStartX.current == null) return;

      const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
      const delta = endX - touchStartX.current;
      touchStartX.current = null;

      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
      if (delta < 0) goNext();
      else goPrev();
   };

   return (
      <div className="sWeekCal">
         <StudentWeekToolbar
            label={rangeLabel}
            onToday={goToday}
            onPrev={goPrev}
            onNext={goNext}
         />

         <div
            className="sWeekCal__viewport"
            style={{ "--visible-count": visibleCount }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
         >
            <div className="sWeekCal__window">
               {visibleDays.map((day) => (
                  <DayColumn
                     key={day.toDateString()}
                     day={day}
                     slotMap={eventsByDay.get(day.toDateString()) ?? EMPTY_SLOT_MAP}
                     onSelectEvent={onSelectEvent}
                  />
               ))}
            </div>
         </div>
      </div>
   );
}
